<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card
			id="hotkeys-module"
			ref="rootCard"
			elevation="1"
			class="pa-0 d-flex flex-column flex-nowrap fill-height"
		>
			<Toolbar :panel-params="params" />
			<Toolbar v-if="selectedStation" :items="headerItems" />
			<NoStationSelectedContainer v-if="!selectedStation" />
			<perfect-scrollbar class="h-100">
				<v-container id="hotkeys-container" class="d-flex flex-column pa-2 pa-xl-3 h-100">
					<div v-if="selectedPage.HotKeys.loaded" class="h-100">
						<v-container
							v-if="soundCardsEnabled"
							id="sound-cards-container"
							class="d-flex flex-column justify-space-between pa-0 h-100"
						>
							<v-card
								v-for="(soundCard, indexChannel) in soundCards"
								:id="`sound-card-${indexChannel}`"
								:key="indexChannel"
								flat
								variant="outlined"
								class="sound-card"
								:height="`${soundCardHeight(indexChannel)}%`"
							>
								<v-tooltip location="bottom">
									<template #activator="{props}">
										<div v-bind="props" class="sound-card-name text-h2">
											{{ soundCardName(indexChannel) }}
										</div>
									</template>
									<span>
										{{ hotkeysOutputDevices[indexChannel]?.name ?? "Out absent" }}
									</span>
								</v-tooltip>
								<div
									v-if="indexChannel < soundCards.length - 1"
									class="resizer"
									@mousedown="resizeSoundCard($event, indexChannel)"
								></div>
							</v-card>
						</v-container>

						<v-container class="d-flex flex-column justify-space-between pa-0 h-100">
							<v-card
								v-for="(hotkeys, indexChannel) in soundCards"
								:key="indexChannel"
								class="sound-card"
								color="transparent"
								flat
								:height="`${soundCardHeight(indexChannel)}%`"
							>
								<draggable
									class="drop-container fill-height d-flex justify-space-between flex-wrap align-center"
									:class="{'pl-10 pl-xl-14 pr-2': soundCardsEnabled}"
									:list="hotkeys"
									draggable=".draggable"
									filter=".empty-slot"
									:group="{
										name: 'hotkeys',
										pull: ['hotkeys', 'playlist', 'log'],
										put: ['library-assets', 'hotkeys', 'playlist', 'log']
									}"
									:force-fallback="true"
									:fallback-tolerance="20"
									:fallback-on-body="true"
									:move="onDragHotKey"
									style="width: 100%"
									drag-class="dragged-hotkey-event"
									data-drop-container="hotkeys"
									@end="onDropHotKey"
								>
									<v-card
										v-for="event in hotkeys"
										:id="event.EventID"
										:key="`${event.row}-${event.column}`"
										class="hotkey"
										:class="{
											draggable: !selectedPage.locked && isDraggable(event),
											'empty-slot': event.empty || event.loading
										}"
										:variant="event.empty ? 'outlined' : 'elevated'"
										:ripple="false"
										:width="100 / selectedPage.Columns - 1 + '%'"
										:data-row="event.row"
										:data-column="event.column"
										:style="{
											height: `calc(${rowHeight(indexChannel)}% - ${xlAndUpScreen ? 16 : 8}px)`
										}"
									>
										<HotKeyItem
											:selected-station="selectedStation"
											:hot-key="event"
											:page="selectedPage"
											:bank="indexChannel"
											:hotkey-output-device="hotkeysOutputDevices[indexChannel]"
											:audition-output-device="auditionOutputDevice"
											:max-gain="hotKeyMaxGain.get(indexChannel)"
											:fade-out-checkbox-enabled="fadeOutCheckboxEnabled"
											@override-hotkey-item="overrideFromHotkeyItem"
										/>
									</v-card>
								</draggable>
							</v-card>
						</v-container>
					</div>
				</v-container>
				<Loader :value="selectedStation && !selectedPage.HotKeys.loaded" />
			</perfect-scrollbar>

			<ManageHotKeyPageDialog
				v-if="addEditDialog.open"
				:dialog="addEditDialog.open"
				:options="addEditDialog.options"
				:selected-station-i-d="selectedStationID"
				@close="addEditDialog.open = false"
				@delete="openDialog('delete', ...arguments)"
			/>
			<DeleteDialog
				v-if="deleteDialog.open"
				:dialog="deleteDialog.open"
				:options="deleteDialog.options"
				@close="
					() => {
						deleteDialog.open = false;
						addEditDialog.open = false;
					}
				"
			/>
			<OverrideHotKeyConfirmationDialog
				v-if="overrideDialog.open"
				:dialog="overrideDialog.open"
				:options="overrideDialog.options"
				@close="overrideDialog.open = false"
			/>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import {VueDraggableNext} from "vue-draggable-next";
import emitter from "@/emitter";
import moment from "moment";

/* Modules */
import {VMenu, VSelect, VList, VListItem, VTextField, VCheckbox, VTooltip, VBtn, VIcon} from "vuetify/components";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import HotKeyItem from "@/components/HotKeyItem.vue";
import ManageHotKeyPageDialog from "@/components/dialogs/ManageHotKeyPageDialog.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import OverrideHotKeyConfirmationDialog from "@/components/dialogs/OverrideHotKeyConfirmationDialog.vue";
import Toolbar from "@/components/Toolbar.vue";

/* Mixins */
import PanelPropertiesMixin from "@/mixins/PanelProperties";
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";

const PANEL_MIN_WIDTH = 600;

export default {
	name: "HotKeys",

	components: {
		draggable: VueDraggableNext,
		Toolbar,
		HotKeyItem,
		Loader,
		NoStationSelectedContainer,
		ManageHotKeyPageDialog,
		DeleteDialog,
		OverrideHotKeyConfirmationDialog
	},

	mixins: [PanelPropertiesMixin, SequencerPlayerStudioStatusMixin],

	props: ["params"],

	data() {
		return {
			addEditDialog: {
				open: false,
				options: {}
			},
			deleteDialog: {
				open: false,
				options: {}
			},
			overrideDialog: {
				open: false,
				options: {}
			},
			pageNameFilterValue: null,
			draggedHotKey: null,
			dropTargetHotKey: null,
			soundCards: [],
			hotkeysMidiBC: null,
			audioOutputType: new Map(),
			hotKeyMaxGain: new Map(),
			fadeOutCheckboxEnabled: this.$store.getters.userPreferences("enableHotkeysFadeOut") ?? false
		};
	},

	computed: {
		headerItems() {
			return [
				{
					component: VSelect,
					props: {
						modelValue: this.selectedPageId,
						items: this.pages,
						itemTitle: "Title",
						itemValue: "PlaylistID",
						label: "Select Page",
						density: "compact",
						variant: "underlined",
						menuIcon: "mdi-chevron-down",
						singleLine: true,
						hideDetails: true,
						ref: "pagesSelector"
					},
					slots: [
						{
							name: "prepend-item",
							items: [
								{
									component: VTextField,
									props: {
										modelValue: this.pageNameFilterValue,
										label: "Search",
										placeholder: "Filter",
										variant: "outlined",
										density: "compact",
										clearable: true,
										prependInnerIcon: "mdi-magnify",
										autofocus: true,
										hideDetails: true,
										singleLine: true
									},
									actions: {
										"update:model-value": value => {
											this.pageNameFilterValue = value;
										}
									}
								}
							],
							actions: {
								"keyup.enter": () => {
									!!this.pageNameFilterValue && this.loadPage();
								}
							}
						},
						{
							name: "item",
							items: data => {
								return [
									{
										component: VListItem,
										props: {...data.props}
									}
								];
							}
						}
					],
					actions: {
						"update:model-value": pageId => {
							this.onChangePage(pageId);
						}
					},
					outerClass: ["w-33"]
				},
				{
					component: VTooltip,
					props: {
						location: "bottom",
						maxWidth: "350px"
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: VCheckbox,
									props: {
										...data.props,
										modelValue: this.enableHotkeysFadeOut,
										class: ["d-flex", "align-center"],
										label: "Auto Fade Hotkey",
										density: "compact",
										hideDetails: true
									},
									actions: {
										"update:model-value": value => {
											this.enableHotkeysFadeOut = value;
											this.fadeOutChanged(value);
										}
									}
								}
							]
						},
						{
							name: "default",
							items: [
								{
									component: "div",
									props: {
										innerHTML: `
											<strong>Checked</strong> - Audio from currently playing hotkey will fade out <br/>
											<strong>Not checked</strong> - Currently playing hotkey will play along with new hotkey
										  `
									}
								}
							]
						}
					],
					outerClass: ["w-50"]
				},
				{
					component: VBtn,
					props: {
						icon: true,
						density: "compact",
						variant: "plain"
					},
					slots: [
						{
							name: "default",
							items: [
								{
									component: VIcon,
									props: {
										icon: this.selectedPage.locked
											? "mdi-lock-outline"
											: "mdi-lock-open-variant-outline",
										size: 24
									}
								}
							]
						}
					],
					actions: {
						click: this.toggleLocking
					},
					outerClass: ["w-25", "text-right"]
				},
				{
					component: VMenu,
					slots: [
						{
							name: "activator",
							items: data => {
								return [
									{
										component: VBtn,
										props: {
											...data.props,
											icon: "mdi-dots-vertical",
											density: "compact",
											variant: "plain"
										}
									}
								];
							}
						},
						{
							name: "default",
							items: [
								{
									component: VList,
									slots: [
										{
											name: "default",
											items: [
												{
													component: VListItem,
													props: {
														title: "Edit Page",
														disabled: !this.selectedStation
													},
													actions: {
														click: () => {
															this.openDialog("edit");
														}
													}
												},
												{
													component: VListItem,
													props: {
														title: "Add New Page",
														disabled: !this.selectedStation
													},
													actions: {
														click: () => {
															this.openDialog("add");
														}
													}
												},
												{
													component: VListItem,
													props: {
														title: "Delete Page",
														disabled: !this.selectedStation
													},
													actions: {
														click: () => {
															this.openDialog("delete");
														}
													}
												}
											]
										}
									]
								}
							]
						}
					]
				}
			];
		},

		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		soundCardsEnabled() {
			return this.studioSettings && !_.isEmpty(this.hotkeysOutputDevices);
		},

		pages() {
			let stationPages = this.$store.getters.getPages().filter(p => p.station);
			if (this.pageNameFilterValue) {
				stationPages = stationPages.filter(p =>
					p.Title.toLowerCase().includes(this.pageNameFilterValue.toLowerCase())
				);
			}

			return stationPages.sort((a, b) => a.Title.localeCompare(b.Title));
		},

		selectedPageId() {
			return this.$store.getters.selectedPageId();
		},

		selectedPage() {
			return this.$store.getters.getSelectedPageData();
		},

		auditionOutputDevice() {
			return this.$store.getters.getActiveStudioAuditionOutputDevice();
		},

		hotkeysOutputDevices() {
			return this.$store.getters.getActiveStudioHotkeysOutputDevices();
		},

		hotKeysEvents() {
			let hotkeys = [];

			for (let row = 0; row < this.selectedPage.Rows; row++) {
				for (let column = 0; column < this.selectedPage.Columns; column++) {
					let hotKey = _.uniqBy(this.selectedPage.HotKeys.data.Events, "SequenceNumber").find(
						e => e.SequenceNumber == `${row}.${column}`
					) || {
						empty: true,
						row: row,
						column: column
					};

					hotKey.row = row;
					hotKey.column = column;

					hotkeys.push(hotKey);
				}
			}
			return hotkeys;
		},

		hotKeysRowsPerSoundCard() {
			return this.$store.getters.getHotKeysRowsPerSoundCard();
		},

		clientId() {
			return this.$store.getters.clientId();
		},

		xlAndUpScreen() {
			return this.$vuetify.display.xlAndUp;
		},

		passAudioLabel() {
			if (this.$store.getters.isWebRTCStudio() && this.studioSettings?.pass_hotkey_audio && this.userIsHost) {
				return "(Requires MIC On to play hotkeys on air)";
			}
			return "";
		}
	},

	watch: {
		selectedStation: {
			async handler(station, previousSelectedStation) {
				if (station) {
					if (previousSelectedStation) {
						if (previousSelectedStation.StationID === station.StationID) {
							return;
						}

						const oldPage = this.$store.getters.getSelectedPageData();
						if (oldPage?.PlaylistID) {
							this.unsubscribeFromTopics(oldPage);
						}
					}

					this.$store.dispatch("getHotKeysPages", this.selectedStationID);
				}
			},
			immediate: true
		},

		selectedPageId(pageId, oldPageId) {
			if (pageId) {
				this.$store.dispatch("getHotKeysBySelectedPage");
				// Subscribe to the PlaylistStation and PlaylistEvent topics
				this.$store.dispatch("playlistSubscription", {playlistID: pageId});
				this.$store.dispatch("playlistEventSubscription", {playlistID: pageId});

				if (oldPageId) {
					const oldPage = this.$store.getters.getPageById(oldPageId);
					if (oldPage) {
						this.unsubscribeFromTopics(oldPage);
					}
					this.$store.commit("setPageHotKeys", {pageId: oldPageId, hotKeys: []});
				}
			}
		},

		hotKeysEvents() {
			this.renderSoundCards();
		},

		hotKeysRowsPerSoundCard() {
			this.renderSoundCards();
		},

		hotkeysOutputDevices() {
			this.renderSoundCards(true);
		}
	},

	created() {
		this.$store.commit("setContainerByRef", {ref: "HotKeysComponent", component: this});

		// Handle escape button click while dragging an event - cancel drag
		document.addEventListener("keydown", this.cancelDraggingOnEscape);
	},

	mounted() {
		// Constraints belong to the group, so reapply them whenever the panel is moved into another one.
		this.updateMinWidth();
		this.groupChangeDisposable = this.params?.api?.onDidGroupChange(this.updateMinWidth);

		this.renderSoundCards();

		this.hotkeysMidiBC = new BroadcastChannel(`hotkeys-midi-channel-${this.clientId}`);
		//	"setActiveStudioOutputDevices" message type
		this.hotkeysMidiBC.addEventListener("message", ({data}) => {
			if (data.type === "setHotkeyAudioOutput") {
				this.audioOutputType.set(data.bank, data.value);
			} else if (data.type === "setHotkeyAudioGain") {
				this.hotKeyMaxGain.set(data.bank, data.gain);
			}
		});
	},

	beforeUnmount() {
		this.groupChangeDisposable?.dispose();

		this.$store.commit("unsetContainerByRef", "HotKeysComponent");

		document.removeEventListener("keydown", this.cancelDraggingOnEscape);
		this.hotkeysMidiBC.close();
	},

	methods: {
		onChangePage(pageId) {
			this.$store.commit("setSelectedPageId", pageId);
			this.pageNameFilterValue = null;
			this.$store.commit("resetHotKeysRowsPerSoundCard");
			emitter.emit("page-changed");
		},

		loadPage() {
			// Get the first page from the filtered ones
			const page = this.pages.find(p => !p.header);
			if (page) {
				this.onChangePage(page.PlaylistID);
				this.$refs.pagesSelector.blur();
			}
		},

		rowHeight(soundCardIndex) {
			const soundCardHeightPercentage = this.soundCardHeight(soundCardIndex);
			const soundCardRows = _.uniq(_.map(this.soundCards[soundCardIndex], "row")).length;
			const rowHeight = soundCardHeightPercentage / soundCardRows;
			return (100 * rowHeight) / soundCardHeightPercentage;
		},

		renderSoundCards(reset) {
			let soundCards = [];
			let rowsPerSoundCard = 0;

			if (this.soundCardsEnabled) {
				for (let deviceIndex in this.hotkeysOutputDevices) {
					deviceIndex = parseInt(deviceIndex);
					if (_.isUndefined(this.hotKeysRowsPerSoundCard[deviceIndex]) || reset) {
						rowsPerSoundCard = Math.ceil(this.selectedPage.Rows / this.hotkeysOutputDevices.length);
						this.$store.commit("setHotKeysRowsPerSoundCard", {index: deviceIndex, rows: rowsPerSoundCard});
					} else {
						rowsPerSoundCard = this.hotKeysRowsPerSoundCard[deviceIndex];
					}

					soundCards[deviceIndex] = this.hotKeysEvents.filter(ev => {
						const previousCardsRowsTotal = _.reduce(
							this.hotKeysRowsPerSoundCard,
							function (sum, n, index) {
								return index < deviceIndex ? sum + n : sum;
							},
							0
						);

						return ev.row >= previousCardsRowsTotal && ev.row < rowsPerSoundCard + previousCardsRowsTotal;
					});
				}
			} else {
				soundCards[0] = this.hotKeysEvents;
			}

			this.soundCards = soundCards;
		},

		soundCardHeight(index) {
			const soundCardRows = _.uniq(_.map(this.soundCards[index], "row")).length;
			const emptySoundCardsLength = this.soundCards.filter(s => _.isEmpty(s)).length;

			let height = ((100 - emptySoundCardsLength * 5) * soundCardRows) / this.selectedPage.Rows;
			// in case of empty sound cards, set the minimum height to 5%
			if (!height) {
				height = 5;
			}

			return height;
		},

		soundCardName(index) {
			return (index + 10).toString(36).toUpperCase();
		},

		resizeSoundCard(ev, index) {
			const that = this;
			const soundCardEl = ev.currentTarget.parentElement;

			const startY = ev.clientY;
			const initialHeight = soundCardEl.clientHeight;
			let heightAfterResize, siblingHeightAfterResize;

			const siblingSoundCard = soundCardEl.nextSibling ?? soundCardEl.previousSibling;
			const siblingSoundCardInitialHeight = siblingSoundCard.clientHeight;

			const hotKeyHeight = document.querySelector(".hotkey").clientHeight + 16;

			initDragResize();

			function initDragResize() {
				document.addEventListener("mousemove", mouseMoveHandler, false);
				document.addEventListener("mouseup", stopResize, false);
			}

			function mouseMoveHandler(ev) {
				const dy = ev.clientY - startY;
				heightAfterResize = initialHeight + dy;
				siblingHeightAfterResize = siblingSoundCardInitialHeight - dy;

				soundCardEl.style.height = `${heightAfterResize}px`;
				siblingSoundCard.style.height = `${siblingHeightAfterResize}px`;
			}

			function stopResize() {
				document.removeEventListener("mousemove", mouseMoveHandler, false);
				document.removeEventListener("mouseup", stopResize, false);

				persistLayout();
			}

			function persistLayout() {
				const soundCardRowsAfterResize = Math.max(Math.round(heightAfterResize / hotKeyHeight), 0);
				const siblingSoundCardIndex = siblingSoundCard.id.split("sound-card-")[1];
				const siblingSoundCardRowsAfterResize = Math.max(
					that.hotKeysRowsPerSoundCard[siblingSoundCardIndex] +
						that.hotKeysRowsPerSoundCard[index] -
						soundCardRowsAfterResize,
					0
				);

				if (!_.isNaN(soundCardRowsAfterResize)) {
					that.$store.commit("setHotKeysRowsPerSoundCard", {index: index, rows: soundCardRowsAfterResize});

					that.$store.commit("setHotKeysRowsPerSoundCard", {
						index: siblingSoundCardIndex,
						rows: siblingSoundCardRowsAfterResize
					});
				}
			}
		},

		openDialog(action) {
			switch (action) {
				case "add":
					this.addEditDialog = {
						open: true,
						options: {
							mode: "add",
							object: {}
						}
					};
					break;
				case "edit":
					this.addEditDialog = {
						open: true,
						options: {
							mode: "edit",
							object: this.selectedPage
						}
					};
					break;
				case "delete":
					this.deleteDialog = {
						open: true,
						options: {
							description: "Hot key page",
							object: this.selectedPage,
							objectId: this.selectedPage.PlaylistID,
							action: "deletePage",
							callback: deletedPage => {
								this.unsubscribeFromTopics(deletedPage);
							}
						}
					};
					break;
			}
		},

		toggleLocking() {
			this.$store.commit("toggleLockPage", !this.selectedPage.locked);
		},

		toggleHighlightDroppableContainer(hotKeyEvent, highlight) {
			this.clearDropTargetHighlight();

			if (highlight) {
				const row = hotKeyEvent.SequenceNumber.toString().split(".")[0];
				const column = hotKeyEvent.SequenceNumber.toString().split(".")[1];

				const rootCard = this.$refs.rootCard?.$el || this.$refs.rootCard;
				const targetElement = rootCard.querySelector(`[data-row="${row}"][data-column="${column}"]`);
				targetElement.classList.add("drop-target-container");
				targetElement.style.setProperty("--borderColor", hotKeyEvent.Color);
			}
		},

		onDragHotKey(ev, originalEvent) {
			if (document.querySelector(".dragged-hotkey-event")) {
				const dropContainer = ev.relatedContext.component.$attrs.group.name;
				this.draggedHotKey = ev.draggedContext.element;
				this.dropTargetHotKey = null;
				let playlistDraggedEvent = null;
				let playlistDropTargetEvent = null;

				if (dropContainer === "hotkeys") {
					this.cancelThrottled();

					this.dropTargetHotKey = {
						...ev.relatedContext.element,
						Color: this.draggedHotKey.Color,
						SequenceNumber: `${ev.relatedContext.element.row}.${ev.relatedContext.element.column}`
					};
				} else if (dropContainer === "playlist") {
					playlistDraggedEvent = {
						AssetID: this.draggedHotKey.Asset.AssetID,
						AssetTitle: this.draggedHotKey.Asset.Title
					};

					const targetElement = originalEvent.target;
					if (
						targetElement.classList.contains("spot-block") ||
						targetElement.classList.contains("local-break")
					) {
						const spotBlockPanelElement = targetElement.querySelector(".v-expansion-panel-title");

						if (
							!this.targetElementToExpand ||
							targetElement.getAttribute("id") !== this.targetElementToExpand.getAttribute("id")
						) {
							this.targetElementToExpand = targetElement;
							clearTimeout(this.throttled);
							this.throttled = setTimeout(() => {
								if (!spotBlockPanelElement.classList.contains("v-expansion-panel-title--active")) {
									spotBlockPanelElement.click();
								}
							}, 2000);
						}

						const dragPosition = Math.abs(originalEvent.clientY - ev.relatedRect.top);

						//if drag element is over the spot block event element
						if (dragPosition <= spotBlockPanelElement.offsetHeight) {
							playlistDropTargetEvent = {
								EventID: targetElement.id,
								StationID: targetElement.dataset.stationId,
								PlaylistID: targetElement.dataset.playlistId,
								ChildPlaylistID: targetElement.dataset.childPlaylistId,
								position: dragPosition > spotBlockPanelElement.offsetHeight / 2 ? "after" : "before",
								insideSpotBlockPlaylist:
									targetElement.classList.contains("spot") ||
									targetElement.classList.contains("empty-spot-placeholder") ||
									targetElement.classList.contains("v-expansion-panel--active")
							};
						} else {
							return false;
						}
					} else {
						this.cancelThrottled();

						playlistDropTargetEvent = {
							EventID: targetElement.id,
							StationID: targetElement.dataset.stationId,
							PlaylistID: targetElement.dataset.playlistId,
							ChildPlaylistID: targetElement.dataset.childPlaylistId,
							position: ev.willInsertAfter ? "after" : "before",
							insideSpotBlockPlaylist:
								targetElement.classList.contains("spot") ||
								targetElement.classList.contains("empty-spot-placeholder") ||
								targetElement.classList.contains("v-expansion-panel--active")
						};

						// in case of dragging over a Top Of The Hour row or an empty playlist container,
						// we need to find the proper event to insert it before or after
						const targetElementIsTOH = targetElement.id.includes("top-of-the-hour");
						const targetElementIsEmptyPlaylistContainer = targetElement.id.includes("empty-playlist");

						if (targetElementIsTOH || targetElementIsEmptyPlaylistContainer) {
							const TOHEvent = ev.relatedContext.list.find(e => e.TopOfTheHour);

							let originalTargetEvent;
							let originalTargetPosition = playlistDropTargetEvent.position;

							if (playlistDropTargetEvent.position === "before") {
								originalTargetEvent = TOHEvent.previousEvent;
								originalTargetPosition = "after";
							} else if (
								!this.$store.getters.isPlaylistEmpty({
									stationID: this.selectedStationID,
									playlistID: TOHEvent.PlaylistID
								})
							) {
								originalTargetEvent = TOHEvent.nextEvent ?? TOHEvent.previousEvent;
								originalTargetPosition = TOHEvent.nextEvent ? "before" : "after";
							}

							if (originalTargetEvent) {
								playlistDropTargetEvent = {
									...playlistDropTargetEvent,
									PlaylistID: originalTargetEvent.PlaylistID,
									originalTargetEvent: {
										EventID: originalTargetEvent.EventID,
										position: originalTargetPosition
									}
								};
							}
						}
					}
				} else {
					this.cancelThrottled();
				}

				emitter.emit("playlist-set-dragged-event", playlistDraggedEvent);
				emitter.emit("playlist-set-dragged-clone", ev.draggedContext.element);
				emitter.emit("playlist-set-drop-target-event", playlistDropTargetEvent);
				this.toggleHighlightDroppableContainer(this.dropTargetHotKey, dropContainer === "hotkeys");
			}

			return false;
		},

		onDropHotKey(ev) {
			if (!this.draggedHotKey) {
				return;
			}
			const dropContainer = ev.originalEvent.target
				?.closest(".drop-container")
				?.getAttribute("data-drop-container");

			if (dropContainer === "hotkeys") {
				// Get the element where we stopped the drag (dropToElement), to be able to know if it's the same with the
				// one we were dragging (isDraggedElementSameWithTheDropping)
				// (move event doesn't get triggered when dragging over the element we selected to drag)
				const dropToElement = ev.originalEvent.target.closest(".v-card.draggable");
				const isDraggedElementSameWithTheDropping = dropToElement === ev.item; // ev.item: dragged HTMLElement

				if (this.dropTargetHotKey && !isDraggedElementSameWithTheDropping) {
					this.dropTargetHotKey.SequenceNumber = parseFloat(this.dropTargetHotKey.SequenceNumber);
					this.updateHotKeyPosition(this.draggedHotKey, this.dropTargetHotKey);
				} else {
					this.clearDropTargetHighlight();
				}
			} else if (dropContainer === "playlist") {
				emitter.emit("playlist-set-dragged-event", {
					AssetID: this.draggedHotKey.AssetID,
					AssetTitle: this.draggedHotKey.Asset.Title
				});
				emitter.emit("playlist-on-drop-hotkey-event", ev);
			} else {
				emitter.emit("playlist-set-drop-target-event", null);
				emitter.emit("playlist-set-dragged-event", null);
				this.dropTargetHotKey = null;
				this.toggleHighlightDroppableContainer(this.dropTargetHotKey, dropContainer === "hotkeys");
			}

			this.draggedHotKey = null;
			this.cancelThrottled();
		},

		addHotKey(hotKey, override = false) {
			const existingHotKeyInTheTargetSlot = this.selectedPage.HotKeys.data.Events.find(
				e => e.SequenceNumber === hotKey.SequenceNumber && e.EventID !== hotKey.EventID
			);

			if (!existingHotKeyInTheTargetSlot || override) {
				if (override) {
					this.createHotKey(hotKey, () => {
						this.deleteHotKey(existingHotKeyInTheTargetSlot);
					});
				} else {
					this.createHotKey(hotKey);
				}
			} else {
				this.overrideDialog = {
					open: true,
					options: {
						confirm: () => {
							this.addHotKey(hotKey, true);
						}
					}
				};
			}
		},

		createHotKey(hotKey, successCallback) {
			this.$store
				.dispatch("addHotKey", hotKey)
				.then(response => {
					if (response.status === 200) {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Hot key successfully created!"});

						if (successCallback) {
							successCallback();
						}
					} else {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when trying adding the hotkey."
						});
					}
				})
				.finally(() => this.clearDropTargetHighlight());
		},

		deleteHotKey(hotKeyEvent) {
			this.$store.dispatch("deleteHotKey", hotKeyEvent).then(response => {
				if (response.status !== 200) {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "An error occurred when trying deleting the existing hot key."
					});
				}
			});
		},

		updateHotKeyPosition(initialHotKey, targetHotKey, override = false) {
			if (targetHotKey.empty || override) {
				if (override) {
					this.updateHotKey(
						initialHotKey,
						{
							SequenceNumber: targetHotKey.SequenceNumber,
							row: targetHotKey.row,
							column: targetHotKey.column
						},
						() => {
							this.deleteHotKey(targetHotKey);
						}
					);
				} else {
					this.updateHotKey(initialHotKey, {SequenceNumber: targetHotKey.SequenceNumber});
				}
			} else {
				this.overrideDialog = {
					open: true,
					options: {
						confirm: () => {
							this.updateHotKeyPosition(initialHotKey, targetHotKey, true);
						}
					}
				};
			}
		},

		updateHotKey(initialHotKey, updatedHotKeyData, successCallback) {
			this.$store
				.dispatch("updateHotKey", {hotKeyEvent: initialHotKey, updatedData: updatedHotKeyData})
				.then(response => {
					if (response.status == 200) {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
							message: "Hot key position successfully updated!"
						});

						if (successCallback) {
							successCallback();
						}
					} else {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when trying updating the hotkey."
						});
					}
				})
				.finally(() => {
					this.draggedHotKey = null;
					this.dropTargetHotKey = null;

					this.clearDropTargetHighlight();
				});
		},

		overrideFromHotkeyItem() {
			this.overrideDialog = {
				open: true,
				options: {
					confirm: () => {
						emitter.emit("overrideHotkey");
					}
				}
			};
		},

		cancelDraggingOnEscape(ev) {
			if (ev.key === "Escape") {
				const draggedElement = document.querySelector(".dragged-hotkey-event");

				if (draggedElement) {
					draggedElement.remove();
					this.draggedHotKey = null;
					this.dropTargetHotKey = null;
					emitter.emit("playlist-set-dragged-event", null);
					emitter.emit("playlist-set-drop-target-event", null);

					this.clearDropTargetHighlight();
				}

				this.cancelThrottled();
			}
		},

		cancelThrottled() {
			if (this.targetElementToExpand) {
				this.targetElementToExpand = null;
				clearTimeout(this.throttled);
			}
		},

		clearDropTargetHighlight() {
			if (this.$refs.rootCard) {
				const rootCard = this.$refs.rootCard?.$el || this.$refs.rootCard;
				if (rootCard?.querySelector(".drop-target-container")) {
					rootCard.querySelector(".drop-target-container").classList.remove("drop-target-container");
				}
			}
		},

		unsubscribeFromTopics(page) {
			// Unsubscribe from the PlaylistStation and PlaylistEvent topics
			this.$store.dispatch("playlistSubscription", {playlistID: page.PlaylistID, subscribe: false});
			this.$store.dispatch("playlistEventSubscription", {playlistID: page.PlaylistID, subscribe: false});

			// Unsubscribe from the Asset and StationAsset topics
			for (const hotkey of page.HotKeys.data?.Events ?? []) {
				if (hotkey.AssetID) {
					this.$store.dispatch("assetSubscription", {assetID: hotkey.AssetID, subscribe: false});
					this.$store.dispatch("stationAssetSubscription", {
						assetID: hotkey.AssetID,
						stationID: page.StationID,
						subscribe: false
					});
					this.$store.commit("removeAssetHotkeySubscription", {assetID: hotkey.AssetID});
				}
			}
		},

		isDraggable(event) {
			if (event?.Asset?.KillDateTime) {
				return moment().isBefore(event?.Asset?.KillDateTime);
			}
			return true;
		},

		fadeOutChanged(value) {
			this.$store.commit("setUserPreferences", {enableHotkeysFadeOut: value});
		},

		updateMinWidth() {
			this.params?.api?.group?.api?.setConstraints({minimumWidth: PANEL_MIN_WIDTH});
		}
	}
};
</script>

<style lang="scss" scoped>
#hotkeys-container {
	-webkit-user-select: none; /* Safari */
	-ms-user-select: none; /* IE 10 and IE 11 */
	user-select: none; /* Standard syntax */
}

:deep(.v-card) {
	&.v-card--variant-outlined {
		border-color: $table-line;

		&.sound-card {
			border-width: 0.44px;
			border-color: #f4f5f5;
		}
	}

	&.empty-slot {
		background: transparent;
	}

	&.drop-target-container,
	&.drop-target-container > .v-card {
		border-color: var(--borderColor) !important;
		border-width: 2px;
	}

	&.sound-card {
		background: rgba($grey-200, 0.3);
		display: flex;
		align-items: center;
		position: relative;
		min-height: 35px;

		&:not(:last-child) {
			margin-bottom: 12px;
		}

		&:hover {
			border-color: #d4d5d6;
		}

		.sound-card-name {
			width: 25px;
			height: 28px;
			background: #ffffff;
			color: $grey-400;
			border: 1px solid $table-line;
			border-radius: 2px !important;
			margin: 0 12px;
			text-align: center;
			cursor: default;
			z-index: 2;
		}

		> .resizer {
			width: 100%;
			height: 12px;
			position: absolute;
			left: 0;
			bottom: -12px;
			cursor: row-resize;
		}

		@media only screen and (max-width: 1920px) {
			&:not(:last-child) {
				margin-bottom: 8px;
			}
			.sound-card-name {
				margin: 0 8px;
				width: 21px;
			}
		}
	}
}

#sound-cards-container {
	position: absolute;
	height: 100%;
}

:deep(.v-select#pages-selector) {
	height: 36px;

	.v-select__selection {
		color: $grey-500;
	}

	.v-theme--dark {
		.v-select__selection {
			color: $ihm-white;
		}
	}
}

.v-btn.v-btn--icon {
	color: $grey-300;

	&:hover {
		color: $grey-400;
	}
}
</style>
